import { cloneDeep } from 'lodash';
import { v4 as uuid } from 'uuid';
import pptxToJSONfrom from '@meitu/pptx-to-json';
import { loadImageAsync } from '@meitu/saas-editor-core/es/utils/file-kit';
import { computerBgRatio } from '@meitu/saas-editor-core/es/utils/layer-kit';
import { decomposeLayerTextTransform } from '@meitu/saas-editor-core/es/utils/math-kit/decomposeLayerTextTransform';
import { TextMeasurer } from '@meitu/saas-editor-core/es/utils/text-kit/measure';
import { traverseTrees } from '@meitu/saas-editor-core/es/utils/tree-kit';
import {
    DefaultClientGroupLayer,
    DefaultClientImageLayer,
    DefaultClientTextLayer,
    DefaultClientVectorLayer,
    getDefaultClientBackgroundValue,
} from '@meitu/saas-editor-core/es/version-parse/constants';
import { LATEST_VERSION } from '@meitu/saas-editor-core/es/version-parse/versions';
import {
    IBackgroundLayerProps,
    IImageLayerProps,
    ILayerProps,
    ImgModeType,
    ITextLayerProps,
    IVectorLayerProps,
    VectorType,
} from '@meitu/saas-editor-core/es/version-types';
import { UploadImage } from '../upload-model/type';
import { getSvgUrl, getUrlByImageElement } from './utils/file';

export interface ObjProps {
    [key: string]: any;
}

interface IParentInfo {
    left: number;
    top: number;
    rotate: number;
}

const adjustSvgElement = (svgStr: string) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgStr, 'image/svg+xml');
    const svgElement = doc.documentElement;
    svgElement.setAttribute('style', 'position: absolute;left: 0;top: 0;');
    const pathElem = svgElement.querySelectorAll('path')[0] as SVGPathElement;
    pathElem.setAttribute('data-need-clippath', 'false');
    const shapeStr = pathElem.outerHTML;
    svgElement.innerHTML = '';
    svgElement.innerHTML = shapeStr;

    return svgElement.outerHTML;
};

function replacePlaceholders(svgString: string, colors: string[]) {
    return svgString.replace(/{{colors\[(\d+)\]}}/g, (match, p1) => {
        const index = parseInt(p1, 10);
        return colors[index];
    });
}

function convertSvgToObjectUrl(svgString: string) {
    // 创建 Blob 对象
    const blob = new Blob([svgString], { type: 'image/svg+xml' });
    // 创建对象 URL
    const url = URL.createObjectURL(blob);

    return url;
}

function removeLastNewline(inputString: string) {
    const lastIndex = inputString.lastIndexOf('\n');
    if (lastIndex === -1) {
        return inputString; // 如果没有找到换行符，返回原字符串
    }
    return inputString.substring(0, lastIndex) + inputString.substring(lastIndex + 1);
}

function replaceBrTagsToN(inputString: string) {
    // 使用正则表达式匹配并替换所有 <br>、<br/> 和 <br /> 标签
    return inputString.replace(/<br\s*\/?>/gi, '\n');
}

function percentageStringToNumber(percentageString: string) {
    if (!percentageString) {
        return 0;
    }
    // 去掉百分号
    const numberString = percentageString.replace('%', '');
    // 转换为浮点数并除以100
    const number = parseFloat(numberString) / 100;
    return number;
}

function extractNumberFromString(str: string) {
    // 匹配 {{colors[0]}} 中的数字
    const regex = /{{colors\[(\d+)\]}}/;
    const match = str.match(regex);
    if (match) {
        const number = parseInt(match[1], 10); // 将匹配到的数字部分转换为整数
        return number;
    } else {
        return 0; // 如果不匹配，返回 null
    }
}

const transformLayer = (layer: ILayerProps, transform: ObjProps) => {
    const transformInfo = decomposeLayerTextTransform([
        transform.a,
        transform.b,
        transform.c,
        transform.d,
        transform.tx,
        transform.ty,
    ]);
    const { xScale, yScale, radian: rotate } = transformInfo[0];
    const { x, y } = transformInfo[4];
    const scale = Math.abs(Math.min(xScale, yScale));

    layer.width = layer.width * scale;
    layer.height = layer.height * scale;

    layer.left = layer.left - x;
    layer.top = layer.top - y;
    layer.rotate = rotate;
};

function calculateGradientAngle(x1: number, y1: number, x2: number, y2: number) {
    // 计算水平和垂直变化
    const dx = x2 - x1;
    const dy = y2 - y1;
    // 计算弧度
    const radians = Math.atan2(dy, dx);
    // 将弧度转换为度数
    const degrees = radians * (180 / Math.PI);
    // 确保角度在 [0, 360) 范围内
    return (degrees + 360 + 90) % 360;
}

function getTextEffect(data: ITextLayerProps, textEffect: ObjProps) {
    if (!textEffect.enable) {
        return;
    }

    const { shadow, stroke } = textEffect;
}

async function adjustTextPosition(layer: ILayerProps, originTextLayer: ObjProps) {
    const { height: realHeight } = await TextMeasurer.measure(layer, {
        keepConstantHeight: true,
    });
    const [top, , bottom] = originTextLayer.padding;
    if (originTextLayer.verticalAlign === 'middle' && originTextLayer.writingMode === 'horizontal-tb') {
        layer.top = layer.top + (layer.height - realHeight) / 2;
        layer.top = layer.top + (top - bottom);
    } else if (originTextLayer.verticalAlign === 'bottom' && originTextLayer.writingMode === 'horizontal-tb') {
        layer.top = layer.top + (layer.height - realHeight);
    } else if (originTextLayer.verticalAlign === 'top' && originTextLayer.writingMode === 'horizontal-tb') {
        layer.top = layer.top + top;
    }

    layer.height = realHeight;
}

function parseImageTransform(originImageLayer: ObjProps) {
    if (!originImageLayer.imageTransform) {
        return {
            widthRatio: 1,
            heightRatio: 1,
            flipH: originImageLayer.flipH,
            flipV: originImageLayer.flipV,
            offsetX: 0,
            offsetY: 0,
            scale: 1,
            rotate: 0,
        };
    }

    const { naturalWidth, naturalHeight, width, height } = originImageLayer;
    const contentWidth = naturalWidth * originImageLayer.imageTransform.scale.x;
    const contentHeight = naturalHeight * originImageLayer.imageTransform.scale.y;
    // scale 由 widthRatio 和 heightRatio 计算后，再将 widthRatio 和 heightRatio进行等比缩放
    const widthRatio = contentWidth / width;
    const heightRatio = contentHeight / height;
    const scale = Math.min(widthRatio, heightRatio);

    return {
        offsetX: originImageLayer.imageTransform.position.x / contentWidth,
        offsetY: originImageLayer.imageTransform.position.y / contentHeight,
        rotate: 0,
        scale,
        flipH: originImageLayer.flipH,
        flipV: originImageLayer.flipV,
        widthRatio: widthRatio / scale,
        heightRatio: heightRatio / scale,
    };
}

export class ParsePPTDataToEditorData {
    static async parseBgLayer(slide: ObjProps, uploadFile?: UploadImage) {
        const { width, height, backgroundGradient, backgroundColor, backgroundImg } = slide;
        const bgLayer = getDefaultClientBackgroundValue(width, height);
        bgLayer.id = uuid();
        const data = bgLayer.data as IBackgroundLayerProps;

        if (backgroundImg && uploadFile && backgroundImg.url) {
            const imgElem = await loadImageAsync(backgroundImg.url);
            data.naturalHeight = imgElem.height;
            data.naturalWidth = imgElem.width;
            console.log('backgroundImg===', backgroundImg);
            // TODO: 背景图片的解析当前还有问题
            data.imageTransform = {
                ...data.imageTransform,
                ...computerBgRatio({
                    imgWidth: data.naturalWidth,
                    imgHeight: data.naturalHeight,
                    stageWidth: bgLayer.width,
                    stageHeight: bgLayer.height,
                }),
            };
            data.url = await getUrlByImageElement(bgLayer.title, imgElem, uploadFile);
        } else if (backgroundGradient) {
            data.backgroundGradient = {
                angle: backgroundGradient.angle + 90,
                colors: backgroundGradient.stops.map((item: ObjProps) => {
                    return {
                        color: item.color,
                        value: item.offset,
                    };
                }),
            };
            data.backgroundColor = '';
        } else if (backgroundColor) {
            data.backgroundColor = backgroundColor || '#FFFFFF';
        }

        console.log('bgLayer===', bgLayer);
        return bgLayer;
    }

    static async parseImageLayer(originImageLayer: ObjProps, parentInfo?: IParentInfo, uploadFile?: UploadImage) {
        const imageLayer = cloneDeep(DefaultClientImageLayer);
        console.log('originImageLayer===', originImageLayer);
        imageLayer.width = originImageLayer.width;
        imageLayer.height = originImageLayer.height;
        imageLayer.id = uuid();
        imageLayer.left = originImageLayer.left;
        imageLayer.top = originImageLayer.top;
        imageLayer.title = originImageLayer.name || originImageLayer.title || '图片图层';
        // imageLayer.rotate = originImageLayer.rotate;
        imageLayer.opacity = originImageLayer.opacity || 1;
        if (originImageLayer.transform) {
            transformLayer(imageLayer, originImageLayer.transform);
        }

        if (parentInfo) {
            imageLayer.left = imageLayer.left + parentInfo.left;
            imageLayer.top = imageLayer.top + parentInfo.top;
            imageLayer.rotate = imageLayer.rotate + parentInfo.rotate;
        }
        const data = imageLayer.data as IImageLayerProps;
        data.url = `${originImageLayer.url}`;

        if (!originImageLayer.url) {
            return imageLayer;
        }
        const imgElem = await loadImageAsync(data.url);
        if (uploadFile) {
            data.url = await getUrlByImageElement(imageLayer.title, imgElem, uploadFile);
        }

        data.naturalHeight = imgElem.height;
        data.naturalWidth = imgElem.width;
        console.log('imgLayer===', imageLayer);

        data.imageTransform = parseImageTransform(originImageLayer);

        // if (originImageLayer.imageTransform?.position) {
        //     const { x, y } = originImageLayer.imageTransform?.position;
        //     data.imageTransform.offsetX =
        //         x / (imageLayer.width * data.imageTransform.widthRatio);
        //     data.imageTransform.offsetY =
        //         y / (imageLayer.height * data.imageTransform.heightRatio);
        // }
        return imageLayer;
    }

    static async parseMaskLayer(originImageLayer: ObjProps, parentInfo?: IParentInfo, uploadFile?: UploadImage) {
        const imageLayer = await ParsePPTDataToEditorData.parseImageLayer(originImageLayer, parentInfo, uploadFile);

        if (originImageLayer.mask) {
            const data = imageLayer.data as IImageLayerProps;

            if (uploadFile) {
                const base64Data = originImageLayer.mask.split(',')[1];
                const svgString = atob(base64Data);
                data.maskURL = await getSvgUrl(svgString, uploadFile);
            }
            originImageLayer.mode = ImgModeType.imgContainer;
        }

        imageLayer.mode = ImgModeType.imgContainer;
        return imageLayer;
    }

    static async parseTextLayer(originTextLayer: ObjProps, parentInfo?: IParentInfo, uploadFile?: UploadImage) {
        console.log('originTextLayer===', originTextLayer);
        const textLayer = cloneDeep(DefaultClientTextLayer);
        textLayer.width = originTextLayer.width;
        textLayer.height = originTextLayer.height;
        textLayer.id = uuid();

        textLayer.left = originTextLayer.left;
        textLayer.top = originTextLayer.top;

        // if (originTextLayer.padding) {
        //     const [top] = originTextLayer.padding;
        //     textLayer.top = textLayer.top + top;
        // }
        if (originTextLayer.transform) {
            transformLayer(textLayer, originTextLayer.transform);
        }
        const data = textLayer.data as ITextLayerProps;

        if (originTextLayer.textEffects && originTextLayer.textEffects[0]) {
            const textEffect = originTextLayer.textEffects[0];
            getTextEffect(data, textEffect);
        }

        if (originTextLayer.textAlign) {
            data.textAlign = originTextLayer.textAlign;
        }

        if (originTextLayer.writingMode) {
            data.writingMode = originTextLayer.writingMode;
        }

        if (originTextLayer.listStyle) {
            data.listStyle = originTextLayer.listStyle;
        }

        const contents = originTextLayer.contents;
        if (contents.length > 1) {
            data.textContents = [];

            for (let i = 0; i < contents.length; i++) {
                const textContent = {} as ObjProps;
                const textInfo = contents[i];
                textContent.text = replaceBrTagsToN(textInfo.content) || '';
                textContent.fontFamily = textInfo.fontFamily || '';
                textContent.fontSize = textInfo.fontSize || 12;
                textContent.italic = textInfo.fontStyle.includes('italic');
                textContent.bold = textInfo.fontWeight > 500;
                textContent.lineThrough = textInfo.textDecoration.includes('line-through');
                textContent.underline = textInfo.textDecoration.includes('underline');
                textContent.color = textInfo.color || '#000000';
                data.textContents.push(textContent as any);

                if (i === 0) {
                    const textInfo = contents[0];
                    data.text = replaceBrTagsToN(textInfo.content) || '';
                    data.fontFamily = textInfo.fontFamily;
                    data.fontSize = textInfo.fontSize || 12;
                    data.italic = textInfo.fontStyle.includes('italic');
                    data.bold = textInfo.fontWeight > 500;
                    data.lineThrough = textInfo.textDecoration.includes('line-through');
                    data.underline = textInfo.textDecoration.includes('underline');
                    data.color = textInfo.color || '#000000';
                    data.letterSpacing = textInfo.letterSpacing || 0;
                }

                if (i === contents.length - 1) {
                    textContent.text = removeLastNewline(textContent.text);
                }
            }
        } else if (contents.length === 1) {
            const textInfo = contents[0];
            data.text = removeLastNewline(replaceBrTagsToN(textInfo.content)) || '';
            data.fontFamily = textInfo.fontFamily || '';
            data.fontSize = textInfo.fontSize || 12;
            data.italic = textInfo.fontStyle.includes('italic');
            data.bold = textInfo.fontWeight > 500;
            data.lineThrough = textInfo.textDecoration.includes('line-through');
            data.underline = textInfo.textDecoration.includes('underline');
            data.color = textInfo.color || '#000000';
            data.letterSpacing = textInfo.letterSpacing || 0;
            if (textInfo.backgroundColor) {
                data.textBackground = [
                    {
                        color: textInfo.backgroundColor,
                        hidden: false,
                    },
                ];
            }
        }

        if (parentInfo) {
            textLayer.rotate = parentInfo.rotate + textLayer.rotate;
            textLayer.left = parentInfo.left + textLayer.left;
            textLayer.top = parentInfo.top + textLayer.top;
        }
        textLayer.title = originTextLayer.name || originTextLayer.title || data.text || '文本图层';
        data.lineSpacing = originTextLayer.lineHeight;
        // 垂直对齐后续统一在 @meitu/pptx-to-json 中处理
        // adjustTextPosition(textLayer, originTextLayer);
        console.log('textLayer===', textLayer);
        return textLayer;
    }

    static async parseVectorLayer(originVectorLayer: ObjProps, parentInfo?: IParentInfo, uploadFile?: UploadImage) {
        console.log('originVectorLayer===', originVectorLayer);
        const vectorLayer = cloneDeep(DefaultClientVectorLayer);

        vectorLayer.width = originVectorLayer.width;
        vectorLayer.height = originVectorLayer.height;
        vectorLayer.id = uuid();
        vectorLayer.left = originVectorLayer.left;
        vectorLayer.top = originVectorLayer.top;
        vectorLayer.title = originVectorLayer.name || originVectorLayer.title || 'svg图层';

        vectorLayer.opacity = originVectorLayer.opacity || 1;

        if (originVectorLayer.transform) {
            transformLayer(vectorLayer, originVectorLayer.transform);
        }

        if (parentInfo) {
            vectorLayer.rotate = parentInfo.rotate + vectorLayer.rotate;
            vectorLayer.left = parentInfo.left + vectorLayer.left;
            vectorLayer.top = parentInfo.top + vectorLayer.top;
        }

        const data = vectorLayer.data as IVectorLayerProps;
        data.stroke = originVectorLayer.shapeBorderColor || '';
        data.strokeWidth = originVectorLayer.borderWidth ? originVectorLayer.borderWidth : 0;

        if (originVectorLayer.flipH) {
            data.imageTransform.flipH = true;
        }

        if (originVectorLayer.flipV) {
            data.imageTransform.flipV = true;
        }

        const viewBoxRegex = /viewBox="([^"]*)"/;
        const match = originVectorLayer.content.match(viewBoxRegex);
        if (match) {
            const viewBoxValue = match[1];
            const [, , width, height] = viewBoxValue.split(' ');
            data.naturalWidth = Math.ceil(width || 0);
            data.naturalHeight = Math.ceil(height || 0);
        }

        if (originVectorLayer.$shapeType === 'rect') {
            data.vectorType = VectorType.Rect;
        } else if (originVectorLayer.$shapeType === 'roundRect') {
            data.vectorType = VectorType.Rect;
            data.radius = originVectorLayer.borderRadius;
        } else if (originVectorLayer.$shapeType === 'ellipse') {
            data.vectorType = VectorType.Ellipse;
        } else {
            data.vectorType = VectorType.Polygon;

            if (uploadFile) data.url = await getSvgUrl(adjustSvgElement(originVectorLayer.content), uploadFile);
        }

        // data.colors = originVectorLayer.colors;
        if (originVectorLayer.gradient) {
            const fillGradient = originVectorLayer.gradient;
            const child = fillGradient['defs']['children'][0];
            const { x1, y1, x2, y2 } = child.attrs;
            data.fillGradient = {
                angle: calculateGradientAngle(
                    percentageStringToNumber(x1),
                    percentageStringToNumber(y1),
                    percentageStringToNumber(x2),
                    percentageStringToNumber(y2),
                ),
                colors: child.children.map((item: any) => {
                    return {
                        color: originVectorLayer.colors[extractNumberFromString(item.attrs['stop-color'])],
                        value: percentageStringToNumber(item.attrs.offset),
                    };
                }),
            };
        } else if (originVectorLayer.$fill) {
            data.fill = originVectorLayer.$fill;
        }

        if (data.vectorType === VectorType.Rect || data.vectorType === VectorType.Ellipse) {
            if (data.fill === 'transparent' && (data.strokeWidth === 0 || data.stroke === '')) {
                return null;
            }
        }
        console.log('vectorLayer===', vectorLayer);
        return vectorLayer;
    }

    static async parseGroupLayer(
        originGroupLayer: ObjProps,
        stageWidth: number,
        stageHeight: number,
        parentInfo?: IParentInfo,
        uploadFile?: UploadImage,
    ) {
        const groupLayer = cloneDeep(DefaultClientGroupLayer);
        const transform = originGroupLayer.transform;

        const { elements } = originGroupLayer;
        groupLayer.width = originGroupLayer.width;
        groupLayer.height = originGroupLayer.height;
        groupLayer.id = uuid();
        groupLayer.left = originGroupLayer.left;
        groupLayer.top = originGroupLayer.top;
        groupLayer.title = originGroupLayer.title || originGroupLayer.name || '图层组';
        groupLayer.rotate = 0;
        groupLayer.lock = originGroupLayer.lock || false;
        groupLayer.opacity = groupLayer.opacity || 1;
        if (originGroupLayer.transform) {
            transformLayer(groupLayer, originGroupLayer.transform);
        }
        // groupLayer.hidden = originGroupLayer.hidden;
        // console.log('originGroupLayer===', originGroupLayer);

        if (parentInfo) {
            groupLayer.rotate = parentInfo.rotate + groupLayer.rotate;
            groupLayer.left = parentInfo.left + groupLayer.left;
            groupLayer.top = parentInfo.top + groupLayer.top;
        }

        if (!elements) {
            return groupLayer;
        }

        for (let i = 0; i < elements.length; i++) {
            // 过滤隐藏图层
            if (elements[i].hidden) continue;
            const childLayer = await ParsePPTDataToEditorData.parseLayer(
                elements[i],
                stageWidth,
                stageHeight,
                {
                    left: groupLayer.left,
                    top: groupLayer.top,
                    rotate: groupLayer.rotate,
                },
                uploadFile,
            );

            if (childLayer) {
                childLayer.parentId = groupLayer.id;
                groupLayer.children.push(childLayer);
            }
        }

        if (groupLayer.children.length === 1) {
            const layer = groupLayer.children[0];
            layer.parentId = '';
            return layer;
        }
        return groupLayer;
    }

    static async parseLayer(
        layer: ObjProps,
        stageWidth: number,
        stageHeight: number,
        parentInfo?: IParentInfo,
        uploadFile?: UploadImage,
    ) {
        // type === mask
        // if (layer.layerType === 'bg') {
        //     return await ParsePPTDataToEditorData.parseBgLayer(
        //         layer,
        //         stageWidth,
        //         stageHeight
        //     );
        // } else
        if (layer.type === 'image') {
            const result = await ParsePPTDataToEditorData.parseImageLayer(layer, parentInfo, uploadFile);
            return result;
        } else if (layer.type === 'text') {
            const result = await ParsePPTDataToEditorData.parseTextLayer(layer, parentInfo, uploadFile);
            return result;
        } else if (layer.type === 'svg') {
            const result = await ParsePPTDataToEditorData.parseVectorLayer(layer, parentInfo, uploadFile);
            return result;
        } else if (layer.type === 'group') {
            return await ParsePPTDataToEditorData.parseGroupLayer(
                layer,
                stageWidth,
                stageHeight,
                parentInfo,
                uploadFile,
            );
        } else if (layer.type === 'mask') {
            const result = await ParsePPTDataToEditorData.parseMaskLayer(layer, parentInfo, uploadFile);
            return result;
        }

        return null;
    }

    static async parseLayers(originSlide: ObjProps, uploadFile?: UploadImage) {
        const { width, height, elements: originLayers } = originSlide;
        const clientLayers = [] as ILayerProps[];

        for (let i = 0; i < originLayers.length; i++) {
            const originLayer = originLayers[i];
            // 过滤隐藏图层
            if (originLayer.hidden) continue;

            const clientLayer = await ParsePPTDataToEditorData.parseLayer(
                originLayer,
                width,
                height,
                undefined,
                uploadFile,
            );
            clientLayer && clientLayers.push(clientLayer);
        }

        return clientLayers;
    }

    static async parse(
        file: File,
        uploadFile?: UploadImage,
        hooks?: {
            onProgress?: (progressData: any) => void;
            onError?: (errorInfo: any) => void;
        },
    ) {
        console.time('pptxToJSON解析时间===');
        const parseData = await pptxToJSONfrom(file, {
            fontList: [],
            onProgress: (data: any) => {
                hooks?.onProgress?.(data);
            },
            onError: (data: any) => {
                hooks?.onError?.(data);
            },
        });
        console.timeEnd('pptxToJSON解析时间===');
        if (!parseData) {
            return null;
        }

        const {
            layouts: slides,
            global: { dpi },
        } = parseData;
        console.log('parseData===', parseData);
        const templateConf = [] as any;
        const version = LATEST_VERSION;
        const preview = '';

        for (let i = 0; i < slides.length; i++) {
            const slide = slides[i];
            const { width, height } = slide;

            const resultLayers = (await ParsePPTDataToEditorData.parseLayers(
                slides[i] as ObjProps,
                uploadFile,
            )) as ILayerProps[];

            const bgLayer = await ParsePPTDataToEditorData.parseBgLayer(slide, uploadFile);

            if (bgLayer) {
                resultLayers.unshift(bgLayer);
            }

            traverseTrees(resultLayers, (layer: ILayerProps) => {
                if (layer.width === 1 && layer.height === 1) {
                    layer.width = width;
                    layer.height = height;
                }
            });

            templateConf.push({
                width,
                height,
                name: '',
                layers: resultLayers,
                settingType: null,
            });
        }

        return {
            version,
            preview,
            editMode: 4,
            templateConf,
            resolution: dpi,
            unit: 'px',
        };
    }
}
